import { Typography, Button, Tag } from "antd";
import {
  LockOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { StepCardActions } from "../AwarnessSections/SectionOne.default";

// PD-02 §6.9.1 Standard Statuses — the four reachable states for a step card.
// "blocked" and "overdue" extend the original locked/current/completed set so
// the operator can tell WHY a step is stuck without digging through logs.
export type StepStatus =
  | "completed"
  | "current"
  | "locked"
  | "blocked"
  | "overdue"
  | "pending"
  | "";

export interface StepCardProps {
  readonly countValue: number;
  readonly status: StepStatus;
  readonly title: string;
  readonly checkListTitle: string;
  readonly checkList: React.ReactNode;
  readonly backgroundColor: string;
  readonly actions: StepCardActions[];
  // Small hint shown under the action button (e.g. "Last updated …").
  readonly actionHint?: string;
  // Human-readable reason displayed next to Blocked/Overdue badges.
  readonly statusReason?: string;
}

function StepCard(props: StepCardProps) {
  const locked = props.status === "locked";
  const blocked = props.status === "blocked";
  const overdue = props.status === "overdue";
  const dimmed = locked || blocked;
  return (
    <article
      className={`animate__animated animate__fadeInUp mx-auto my-6 flex justify-between rounded-xl px-20 py-4 text-white shadow-xl shadow-slate-400/50 ${overdue ? "pulse-coral" : ""}`}
      style={{
        backgroundColor: `${props.backgroundColor}`,
        opacity: dimmed ? 0.55 : 1,
        outline: overdue ? "2px solid #f06364" : undefined,
      }}
    >
      {/* Left Side */}
      <section className="flex basis-[15%] flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center self-center rounded-xl bg-[#d9d9d9] px-9 py-6 text-3xl font-bold text-black">
          {props.countValue}
        </div>
        <p className="text-lg font-bold tracking-wide">
          {props?.status.toLocaleUpperCase()}
        </p>
        {blocked && (
          <Tag
            color="orange"
            icon={<ExclamationCircleOutlined />}
            className="!mt-1"
          >
            BLOCKED
          </Tag>
        )}
        {overdue && (
          <Tag
            color="red"
            icon={<ClockCircleOutlined />}
            className="!mt-1"
          >
            OVERDUE
          </Tag>
        )}
        {(blocked || overdue) && props.statusReason && (
          <span className="mt-1 text-center text-xs text-white/80">
            {props.statusReason}
          </span>
        )}
      </section>
      {/* Middle */}
      <section className="flex basis-[60%] flex-col">
        <Typography.Title
          level={4}
          className="m-0 p-2 tracking-wide text-white"
        >
          {props.title.toUpperCase()}
        </Typography.Title>
        <Typography.Title
          level={5}
          className="m-0 p-2 text-lg tracking-wide text-white"
        >
          {props.checkListTitle}
        </Typography.Title>
        <ol className="m-0 px-4 text-justify text-lg tracking-wide">
          {props.checkList}
        </ol>
      </section>
      {/* Right Side */}
      <section className="flex basis-[15%] flex-col items-center justify-center gap-2">
        {locked ? (
          <LockOutlined className="text-3xl" />
        ) : (
          <>
            {props.actions.map((action, index) => {
              return (
                <Button
                  key={index}
                  className="w-20 bg-white px-16 py-5 text-lg tracking-wide"
                  onClick={action.action}
                >
                  {action.name.toUpperCase()}
                </Button>
              );
            })}
            {props.actionHint && (
              <span className="text-center text-xs text-white/80">{props.actionHint}</span>
            )}
          </>
        )}
      </section>
    </article>
  );
}

export default StepCard;
